import { useEffect, useState, useCallback } from "react";
import { Loader2, Search, FileClock, Wallet, BellRing, RefreshCw, FileText } from "lucide-react";
import { DocMenu } from "@/components/docs/DocMenu";
import { BulkPrintBar } from "@/components/docs/BulkPrintBar";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, fmtDate } from "@/lib/format";
import { AgingBadge, InvStatus, BUCKET_LABEL } from "@/components/finance/aging";
import { PaymentDialog } from "@/components/finance/PaymentDialog";
import { InvoiceHistory } from "@/components/finance/InvoiceHistory";

const CLOSED = ["Lunas", "Dibatalkan"];

export function InvoicesTab({ refreshKey, bucket, setBucket, onChanged }) {
  const { hasPerm } = useAuth();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [payFor, setPayFor] = useState(null);
  const [histFor, setHistFor] = useState(null);
  const [busy, setBusy] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [withReceipts, setWithReceipts] = useState(true);

  const load = useCallback(() => {
    api.get("/finance/invoices", { params: { q: q || undefined, status, bucket } })
      .then((r) => { setRows(r.data.data); setSel(new Set()); }).catch((e) => { toast.error(apiError(e)); setRows([]); });
  }, [q, status, bucket]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, refreshKey]);

  const changed = () => { load(); onChanged(); };
  const remind = async (inv) => {
    setBusy(`r-${inv.id}`);
    try {
      const r = await api.post(`/finance/invoices/${inv.id}/remind`);
      const d = r.data.data;
      if (d.sent) toast.success(`Pengingat dikirim ke ${d.to}`);
      if (d.reason) (d.sent ? toast.message : toast.warning)(`Tidak terkirim — ${d.reason}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  const sendReceipt = async (inv) => {
    try {
      const r = await api.post(`/finance/invoices/${inv.id}/receipt/send`);
      const d = r.data.data;
      d.sent ? toast.success(`Kwitansi ${d.receipt_number} dikirim ke ${d.to}`) : toast.warning(`Kwitansi tidak dikirim: ${d.reason}`);
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const invDocs = (inv) => [
    { kind: "invoice", id: inv.id, label: "Invoice", number: inv.number },
    ...(inv.status === "Lunas" ? [{ kind: "receipt", id: inv.id, label: "Kwitansi", number: inv.receipt_number || `untuk ${inv.number}` }] : []),
  ];
  const refresh = async (inv) => {
    setBusy(`s-${inv.id}`);
    try {
      const r = await api.post(`/finance/invoices/${inv.id}/refresh`);
      toast.success(r.data.meta.changed ? "Status pembayaran diperbarui dari Odoo" : "Sudah sesuai dengan Odoo");
      if (r.data.meta.changed) changed();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  return (
    <div className="panel table-panel" data-testid="invoices-tab">
      <div className="table-tools">
        <div className="search-box"><Search /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari invoice / SO / outlet…" data-testid="invoice-search" /></div>
        <select className="rj-select" style={{ width: 170 }} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="invoice-status-filter">
          <option value="all">Semua Status</option><option>Ditagihkan</option><option>Dibayar Sebagian</option><option>Lunas</option><option>Dibatalkan</option>
        </select>
        <select className="rj-select" style={{ width: 180 }} value={bucket} onChange={(e) => setBucket(e.target.value)} data-testid="invoice-bucket-filter">
          <option value="all">Semua Umur AR</option>
          {Object.entries(BUCKET_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <BulkPrintBar count={sel.size} testId="invoice-bulk-bar" onClear={() => setSel(new Set())}
        summary={<><b data-testid="invoice-bulk-count">{sel.size}</b> invoice dipilih</>}
        body={() => ({ docs: (rows || []).filter((i) => sel.has(i.id)).map((i) => ({ kind: "invoice", id: i.id })), with_receipts: withReceipts })}>
        <label className="autoprint-toggle" data-testid="invoice-bulk-receipts">
          <input type="checkbox" checked={withReceipts} onChange={(e) => setWithReceipts(e.target.checked)} data-testid="invoice-bulk-receipts-toggle" />
          Ikut kwitansi (invoice lunas)
        </label>
      </BulkPrintBar>
      {rows === null ? <div className="rj-loading"><Loader2 className="spin" /> Memuat…</div> : rows.length === 0 ? (
        <div className="rj-empty" data-testid="invoices-empty"><FileText /><p>Belum ada invoice.</p></div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="fin-table">
            <thead><tr><th className="chk-col"><input type="checkbox" checked={rows.length > 0 && rows.every((i) => sel.has(i.id))} aria-label="Centang semua invoice"
              onChange={() => setSel(rows.every((i) => sel.has(i.id)) ? new Set() : new Set(rows.map((i) => i.id)))} data-testid="invoices-select-all" /></th><th>Invoice / Outlet</th><th>Dibuat oleh</th><th>Tgl DO</th><th>Tgl Tagih</th><th>Jatuh Tempo</th><th>Total</th><th>Sisa</th><th>AR Aging</th><th>Status</th><th style={{ textAlign: "right" }}>Aksi</th></tr></thead>
            <tbody>
              {rows.map((inv) => (
                <tr key={inv.id} className={sel.has(inv.id) ? "row-picked" : ""} data-testid={`invoice-row-${inv.number}`}>
                  <td className="chk-col"><input type="checkbox" checked={sel.has(inv.id)} aria-label={`Pilih ${inv.number}`}
                    onChange={() => setSel((s) => { const n = new Set(s); n.has(inv.id) ? n.delete(inv.id) : n.add(inv.id); return n; })} data-testid={`invoice-check-${inv.number}`} /></td>
                  <td><strong>{inv.outlet}</strong><span className="odoo-ref">{inv.number}</span><small className="fin-muted">{inv.so_number}</small></td>
                  <td><b className="fin-actor">{inv.created_by_name}</b><small className="fin-muted">{inv.created_by_email}</small></td>
                  <td><small>{fmtDate(inv.first_do_date)}</small></td>
                  <td><small>{fmtDate(inv.invoice_date)}</small></td>
                  <td><small>{fmtDate(inv.due_date)}</small><small className="fin-muted">{inv.ar_term_name}{inv.ar_term_manual && <span className="bill-chip merged" title="AR Term dipilih manual saat penagihan">manual</span>}</small>
                    {inv.combined && <span className="bill-chip merged" data-testid={`invoice-combined-${inv.number}`}>Gabungan {inv.so_numbers?.length} SO</span>}</td>
                  <td><b>{rupiah(inv.amount_total)}</b></td>
                  <td><b data-testid={`invoice-residual-${inv.number}`}>{rupiah(inv.amount_residual)}</b></td>
                  <td><AgingBadge inv={inv} /><small className="fin-muted">umur {inv.age_days} hari</small></td>
                  <td><InvStatus status={inv.status} testId={`invoice-status-${inv.number}`} /></td>
                  <td>
                    <div className="row-actions">
                      <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={() => setHistFor(inv)} title="Riwayat & rincian" data-testid={`invoice-history-${inv.number}`}><FileClock size={14} /></button>
                      <DocMenu title={`Dokumen ${inv.number}`} docs={invDocs(inv)} testId={`invoice-docs-${inv.number}`}
                        extra={inv.status === "Lunas" && hasPerm("invoices:create") ? [{ label: "Kirim ulang kwitansi via email", icon: "mail", onClick: () => sendReceipt(inv), testId: `invoice-receipt-send-${inv.number}` }] : []} />
                      {hasPerm("payments:verify") && !CLOSED.includes(inv.status) && (
                        <button className="rj-btn rj-btn-green rj-btn-sm" onClick={() => setPayFor(inv)} data-testid={`invoice-pay-${inv.number}`}><Wallet size={14} /> Verifikasi Bayar</button>
                      )}
                      {hasPerm("invoices:create") && !CLOSED.includes(inv.status) && (
                        <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={() => remind(inv)} disabled={busy === `r-${inv.id}`} title="Kirim pengingat sekarang" data-testid={`invoice-remind-${inv.number}`}>
                          {busy === `r-${inv.id}` ? <Loader2 size={14} className="spin" /> : <BellRing size={14} />}
                        </button>
                      )}
                      {inv.odoo_move_id && (
                        <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={() => refresh(inv)} disabled={busy === `s-${inv.id}`} title="Cek status di Odoo" data-testid={`invoice-refresh-${inv.number}`}>
                          {busy === `s-${inv.id}` ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {payFor && <PaymentDialog inv={payFor} onClose={() => setPayFor(null)} onSaved={() => { setPayFor(null); changed(); }} />}
      {histFor && <InvoiceHistory invoiceId={histFor.id} onClose={() => setHistFor(null)} />}
    </div>
  );
}
